/**
 * Admin Dashboard Tab
 * Main dashboard view for WeBeMail Management Console
 * Mirrors Document Management dashboard styling
 */
import React, { useState, useCallback } from 'react';
import {
  ChartBarIcon,
  EnvelopeIcon,
  ServerIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  CloudIcon,
  UserGroupIcon,
  InboxStackIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  EyeIcon,
  ComputerDesktopIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import {
  useDashboardMetrics,
  useSystemHealth,
  useAlerts,
  useAdminActivitySummary,
  useAcknowledgeAlert,
  useInboundQueueHealth,
  useOutboundQueueHealth,
  useConnectedClients,
} from '../../hooks/useAdminDashboard';
import type {
  TenantHealthStatus,
  ComponentStatus,
  AlertSeverity,
  AdminAlert,
  AuditLogSummary,
  QueueHealthStatus,
  OutboundQueueHealthStatus,
  QueueAlert,
  QueueJobStatus,
  QueueType,
} from '../../types/admin.types';
import { QueueJobViewerModal } from '../modals/QueueJobViewerModal';
import { ConnectedClientsPanel } from '../panels/ConnectedClientsPanel';

// Utility functions
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatNumber(num: number): string {
  return new Intl.NumberFormat().format(num);
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// Health status badge component
interface HealthBadgeProps {
  status: TenantHealthStatus | ComponentStatus;
  size?: 'sm' | 'md';
}

const HealthBadge: React.FC<HealthBadgeProps> = ({ status, size = 'md' }) => {
  const styles = {
    healthy: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    operational: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    degraded: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400',
    critical: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    down: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  };

  const icons = {
    healthy: CheckCircleIcon,
    operational: CheckCircleIcon,
    degraded: ExclamationTriangleIcon,
    critical: XCircleIcon,
    down: XCircleIcon,
  };

  const Icon = icons[status] || ExclamationTriangleIcon;
  const sizeClass = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-2.5 py-1 text-sm';

  return (
    <span className={`inline-flex items-center gap-1 rounded-full font-medium ${sizeClass} ${styles[status]}`}>
      <Icon className={size === 'sm' ? 'h-3 w-3' : 'h-4 w-4'} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// Severity badge component
interface SeverityBadgeProps {
  severity: AlertSeverity;
}

const SeverityBadge: React.FC<SeverityBadgeProps> = ({ severity }) => {
  const styles = {
    info: 'bg-lex-accent-soft text-lex-accent',
    warning: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400',
    critical: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[severity]}`}>
      {severity.charAt(0).toUpperCase() + severity.slice(1)}
    </span>
  );
};

// Stat card component
interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ComponentType<{ className?: string }>;
  trend?: { value: number; direction: 'up' | 'down' };
  color?: 'blue' | 'green' | 'amber' | 'red' | 'purple';
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => {
  const colorStyles = {
    blue: 'bg-lex-accent-soft text-lex-accent',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
    red: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className={`p-2 rounded-lg ${colorStyles[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        {trend && (
          <div className={`flex items-center text-xs ${trend.direction === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trend.direction === 'up' ? (
              <ArrowTrendingUpIcon className="h-4 w-4 mr-0.5" />
            ) : (
              <ArrowTrendingDownIcon className="h-4 w-4 mr-0.5" />
            )}
            {trend.value}%
          </div>
        )}
      </div>
      <div className="mt-3">
        <p className="text-2xl font-semibold text-gray-900 dark:text-white">{value}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">{title}</p>
        {subtitle && <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
};

// Storage progress bar component
interface StorageBarProps {
  used: number;
  total: number;
  percentUsed: number;
}

const StorageBar: React.FC<StorageBarProps> = ({ used, total, percentUsed }) => {
  const getBarColor = (percent: number) => {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 75) return 'bg-amber-500';
    return 'bg-lex-accent';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20">
            <CloudIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          </div>
          <span className="font-medium text-gray-900 dark:text-white">Storage Usage</span>
        </div>
        <span className="text-sm text-gray-600 dark:text-gray-400">{percentUsed.toFixed(1)}%</span>
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${getBarColor(percentUsed)}`}
          style={{ width: `${Math.min(percentUsed, 100)}%` }}
        />
      </div>
      <div className="flex justify-between mt-2 text-xs text-gray-500 dark:text-gray-400">
        <span>{formatBytes(used)} used</span>
        <span>{formatBytes(total)} total</span>
      </div>
    </div>
  );
};

// System health component
interface SystemHealthCardProps {
  health: ReturnType<typeof useSystemHealth>['data'];
  isLoading: boolean;
}

const SystemHealthCard: React.FC<SystemHealthCardProps> = ({ health, isLoading }) => {
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm animate-pulse">
        <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex justify-between">
              <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!health) return null;

  const components = [
    { key: 'database', label: 'Database', icon: ServerIcon },
    { key: 'smtp', label: 'SMTP Server', icon: EnvelopeIcon },
    { key: 'imap', label: 'IMAP Server', icon: InboxStackIcon },
    { key: 'storage', label: 'Storage', icon: CloudIcon },
    { key: 'keycloak', label: 'Keycloak', icon: ShieldCheckIcon },
  ] as const;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-green-50 dark:bg-green-900/20">
            <ServerIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
          <span className="font-medium text-gray-900 dark:text-white">System Health</span>
        </div>
        <HealthBadge status={health.overall} />
      </div>
      <div className="space-y-3">
        {components.map(({ key, label, icon: Icon }) => {
          const component = health.components[key];
          return (
            <div key={key} className="space-y-0.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                </div>
                <div className="flex items-center gap-2">
                  {component.latencyMs !== undefined && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">{component.latencyMs}ms</span>
                  )}
                  <HealthBadge status={component.status} size="sm" />
                </div>
              </div>
              {component.message && component.status !== 'operational' && (
                <p className="text-xs text-amber-600 dark:text-amber-400 ml-6">{component.message}</p>
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Last checked: {formatTimeAgo(health.lastChecked)}
        </p>
      </div>
    </div>
  );
};

// Alerts list component
interface AlertsListProps {
  alerts: AdminAlert[];
  isLoading: boolean;
  onAcknowledge: (alertId: string) => void;
  acknowledging: boolean;
}

const AlertsList: React.FC<AlertsListProps> = ({ alerts, isLoading, onAcknowledge, acknowledging }) => {
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm animate-pulse">
        <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <span className="font-medium text-gray-900 dark:text-white">Recent Alerts</span>
        </div>
        <span className="text-sm text-gray-500 dark:text-gray-400">{alerts.length} active</span>
      </div>
      <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-80 overflow-y-auto">
        {alerts.length === 0 ? (
          <div className="p-6 text-center">
            <CheckCircleIcon className="h-10 w-10 text-green-500 mx-auto mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400">No active alerts</p>
          </div>
        ) : (
          alerts.map((alert) => (
            <div key={alert.alertId} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <SeverityBadge severity={alert.severity} />
                    <span className="text-xs text-gray-500 dark:text-gray-400 capitalize">{alert.category}</span>
                  </div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">{alert.title}</h4>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">{alert.description}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{formatTimeAgo(alert.createdAt)}</p>
                </div>
                {!alert.isAcknowledged && (
                  <button
                    onClick={() => onAcknowledge(alert.alertId)}
                    disabled={acknowledging}
                    className="shrink-0 px-2 py-1 text-xs font-medium text-lex-accent hover:bg-lex-state-hover rounded transition-colors disabled:opacity-50"
                  >
                    Acknowledge
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Activity list component
interface ActivityListProps {
  activities: AuditLogSummary[];
  isLoading: boolean;
}

const ActivityList: React.FC<ActivityListProps> = ({ activities, isLoading }) => {
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm animate-pulse">
        <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-lex-accent-soft">
            <ClockIcon className="h-5 w-5 text-lex-accent" />
          </div>
          <span className="font-medium text-gray-900 dark:text-white">Recent Activity</span>
        </div>
      </div>
      <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-80 overflow-y-auto">
        {activities.length === 0 ? (
          <div className="p-6 text-center">
            <ClockIcon className="h-10 w-10 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 dark:text-gray-400">No recent activity</p>
          </div>
        ) : (
          activities.map((activity) => (
            <div key={activity.auditId} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 dark:text-white">
                    <span className="font-medium">{activity.action}</span>
                    {activity.resourceType && (
                      <span className="text-gray-600 dark:text-gray-400"> on {activity.resourceType}</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {activity.adminEmail} &middot; {formatTimeAgo(activity.createdAt)}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// Top storage users component
interface TopStorageUsersProps {
  users: Array<{
    email: string;
    displayName: string;
    usedBytes: number;
    quotaBytes: number;
    percentUsed: number;
  }>;
  isLoading: boolean;
}

const TopStorageUsers: React.FC<TopStorageUsersProps> = ({ users, isLoading }) => {
  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm animate-pulse">
        <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20">
            <UserGroupIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
          </div>
          <span className="font-medium text-gray-900 dark:text-white">Top Storage Users</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3 text-right">Used</th>
              <th className="px-4 py-3 text-right">Quota</th>
              <th className="px-4 py-3 w-32">Usage</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {users.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-600 dark:text-gray-400">
                  No mailbox data available
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.email} className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user.displayName}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-700 dark:text-gray-300">
                    {formatBytes(user.usedBytes)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-500 dark:text-gray-400">
                    {formatBytes(user.quotaBytes)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            user.percentUsed >= 90
                              ? 'bg-red-500'
                              : user.percentUsed >= 75
                              ? 'bg-amber-500'
                              : 'bg-lex-accent'
                          }`}
                          style={{ width: `${Math.min(user.percentUsed, 100)}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 w-10 text-right">
                        {user.percentUsed.toFixed(0)}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Queue health panel component
interface QueueDepthBarProps {
  label: string;
  value: number;
  max: number;
  color: string;
  onClick?: () => void;
  clickable?: boolean;
}

const QueueDepthBar: React.FC<QueueDepthBarProps> = ({ label, value, max, color, onClick, clickable = false }) => {
  const percent = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  const isClickable = clickable && value > 0;

  return (
    <div
      className={`flex items-center gap-3 ${isClickable ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 -mx-2 px-2 py-1 rounded-lg transition-colors group' : ''}`}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); } : undefined}
    >
      <span className="text-xs text-gray-600 dark:text-gray-400 w-20 shrink-0">{label}</span>
      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 w-10 text-right">{formatNumber(value)}</span>
      {isClickable && (
        <EyeIcon className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </div>
  );
};

interface QueueHealthPanelProps {
  inbound: { data: { health: QueueHealthStatus } | undefined; isLoading: boolean };
  outbound: { data: { health: OutboundQueueHealthStatus } | undefined; isLoading: boolean };
  onViewJobs?: (queueType: QueueType, status: QueueJobStatus) => void;
}

const QueueHealthPanel: React.FC<QueueHealthPanelProps> = ({ inbound, outbound, onViewJobs }) => {
  const isLoading = inbound.isLoading || outbound.isLoading;

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 shadow-sm animate-pulse col-span-full">
        <div className="h-6 w-40 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-4 bg-gray-200 dark:bg-gray-700 rounded" />
            ))}
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-4 bg-gray-200 dark:bg-gray-700 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  const ib = inbound.data?.health;
  const ob = outbound.data?.health;

  // Collect all alerts from both queues
  const allAlerts: QueueAlert[] = [
    ...(ib?.alerts || []),
    ...(ob?.alerts || []),
  ];

  const hasAlerts = allAlerts.length > 0;
  const hasCritical = allAlerts.some(a => a.level === 'critical');

  // Determine max for bar scaling
  const inboundMax = Math.max(
    ib?.inbound.waiting || 0,
    ib?.inbound.active || 0,
    ib?.inbound.failed || 0,
    ib?.dlq.waiting || 0,
    50,
  );
  const outboundMax = Math.max(
    ob?.outbound.waiting || 0,
    ob?.outbound.active || 0,
    ob?.outbound.failed || 0,
    ob?.dlq.waiting || 0,
    50,
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm col-span-full">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${hasAlerts ? (hasCritical ? 'bg-red-50 dark:bg-red-900/20' : 'bg-amber-50 dark:bg-amber-900/20') : 'bg-lex-accent-soft'}`}>
            <ChartBarIcon className={`h-5 w-5 ${hasAlerts ? (hasCritical ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400') : 'text-lex-accent'}`} />
          </div>
          <span className="font-medium text-gray-900 dark:text-white">Queue Health</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className={`inline-block h-2 w-2 rounded-full ${ib?.connected ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="text-xs text-gray-500 dark:text-gray-400">Inbound</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className={`inline-block h-2 w-2 rounded-full ${ob?.connected ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="text-xs text-gray-500 dark:text-gray-400">Outbound</span>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Inbound Queue */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">Inbound Queue</h4>
              <div className="flex items-center gap-2">
                {ib?.worker.running ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                    <CheckCircleIcon className="h-3 w-3" /> Worker Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                    <XCircleIcon className="h-3 w-3" /> Worker Stopped
                  </span>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <QueueDepthBar label="Waiting" value={ib?.inbound.waiting || 0} max={inboundMax} color="bg-lex-accent" clickable onClick={() => onViewJobs?.('inbound', 'waiting')} />
              <QueueDepthBar label="Active" value={ib?.inbound.active || 0} max={inboundMax} color="bg-green-500" clickable onClick={() => onViewJobs?.('inbound', 'active')} />
              <QueueDepthBar label="Failed" value={ib?.inbound.failed || 0} max={inboundMax} color="bg-red-500" clickable onClick={() => onViewJobs?.('inbound', 'failed')} />
              <QueueDepthBar label="DLQ" value={ib?.dlq.waiting || 0} max={inboundMax} color="bg-amber-500" clickable onClick={() => onViewJobs?.('inbound', 'dlq')} />
            </div>
            <div className="mt-2 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span>Completed: {formatNumber(ib?.inbound.completed || 0)}</span>
              <span>Concurrency: {ib?.worker.concurrency || 0}</span>
            </div>
          </div>

          {/* Outbound Queue */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">Outbound Queue</h4>
              <div className="flex items-center gap-2">
                {ob?.worker.running ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                    <CheckCircleIcon className="h-3 w-3" /> Worker Active
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                    <XCircleIcon className="h-3 w-3" /> Worker Stopped
                  </span>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <QueueDepthBar label="Waiting" value={ob?.outbound.waiting || 0} max={outboundMax} color="bg-lex-accent" clickable onClick={() => onViewJobs?.('outbound', 'waiting')} />
              <QueueDepthBar label="Active" value={ob?.outbound.active || 0} max={outboundMax} color="bg-green-500" clickable onClick={() => onViewJobs?.('outbound', 'active')} />
              <QueueDepthBar label="Failed" value={ob?.outbound.failed || 0} max={outboundMax} color="bg-red-500" clickable onClick={() => onViewJobs?.('outbound', 'failed')} />
              <QueueDepthBar label="DLQ" value={ob?.dlq.waiting || 0} max={outboundMax} color="bg-amber-500" clickable onClick={() => onViewJobs?.('outbound', 'dlq')} />
            </div>
            <div className="mt-2 flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
              <span>Completed: {formatNumber(ob?.outbound.completed || 0)}</span>
              <span>Concurrency: {ob?.worker.concurrency || 0}</span>
            </div>
          </div>
        </div>

        {/* Queue Alerts */}
        {hasAlerts && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="space-y-2">
              {allAlerts.map((alert, idx) => (
                <div
                  key={`${alert.code}-${idx}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                    alert.level === 'critical'
                      ? 'bg-red-50 dark:bg-red-900/10 text-red-800 dark:text-red-300'
                      : 'bg-amber-50 dark:bg-amber-900/10 text-amber-800 dark:text-amber-300'
                  }`}
                >
                  <ExclamationTriangleIcon className="h-4 w-4 shrink-0" />
                  <span>{alert.message}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Admin Dashboard Tab Component
 */
export const AdminDashboardTab: React.FC = () => {
  const { data: metrics, isLoading: metricsLoading, refetch: refetchMetrics } = useDashboardMetrics();
  const { data: health, isLoading: healthLoading } = useSystemHealth();
  const { data: alerts, isLoading: alertsLoading } = useAlerts({ limit: 10 });
  const { data: activitySummary, isLoading: activityLoading } = useAdminActivitySummary({ days: 7, limit: 10 });
  const acknowledgeAlert = useAcknowledgeAlert();
  const inboundQueueHealth = useInboundQueueHealth();
  const outboundQueueHealth = useOutboundQueueHealth();
  const { data: connectedClients, isLoading: clientsLoading } = useConnectedClients();

  // Queue Job Viewer Modal state
  const [queueViewerOpen, setQueueViewerOpen] = useState(false);
  const [queueViewerConfig, setQueueViewerConfig] = useState<{
    queueType: QueueType;
    status: QueueJobStatus;
  }>({ queueType: 'inbound', status: 'failed' });

  const handleViewJobs = useCallback((queueType: QueueType, status: QueueJobStatus) => {
    setQueueViewerConfig({ queueType, status });
    setQueueViewerOpen(true);
  }, []);

  const handleRefresh = () => {
    refetchMetrics();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Monitor your WeBeMail tenant health, storage, and activity
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={metricsLoading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${metricsLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Overall Health Banner */}
      {health && (
        <div
          className={`rounded-lg p-4 border ${
            health.overall === 'healthy'
              ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
              : health.overall === 'degraded'
              ? 'bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800'
              : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
          }`}
        >
          <div className="flex items-center gap-3">
            {health.overall === 'healthy' ? (
              <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
            ) : health.overall === 'degraded' ? (
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            ) : (
              <XCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
            )}
            <div>
              <p
                className={`font-medium ${
                  health.overall === 'healthy'
                    ? 'text-green-800 dark:text-green-300'
                    : health.overall === 'degraded'
                    ? 'text-amber-800 dark:text-amber-300'
                    : 'text-red-800 dark:text-red-300'
                }`}
              >
                {health.overall === 'healthy'
                  ? 'All systems operational'
                  : health.overall === 'degraded'
                  ? 'Some systems experiencing issues'
                  : 'Critical system issues detected'}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Last checked {formatTimeAgo(health.lastChecked)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Active Mailboxes"
          value={metricsLoading ? '-' : formatNumber(metrics?.activeMailboxes ?? 0)}
          subtitle={metricsLoading ? '' : `${metrics?.totalMailboxes ?? 0} total`}
          icon={EnvelopeIcon}
          color="blue"
        />
        <StatCard
          title="Messages Sent (24h)"
          value={metricsLoading ? '-' : formatNumber(metrics?.messagesSentLast24h ?? 0)}
          icon={ArrowTrendingUpIcon}
          color="green"
        />
        <StatCard
          title="Messages Received (24h)"
          value={metricsLoading ? '-' : formatNumber(metrics?.messagesReceivedLast24h ?? 0)}
          icon={InboxStackIcon}
          color="purple"
        />
        <StatCard
          title="Pending Outbound"
          value={metricsLoading ? '-' : formatNumber(metrics?.pendingOutbound ?? 0)}
          subtitle={metricsLoading ? '' : `${metrics?.deferredMessages ?? 0} deferred`}
          icon={ClockIcon}
          color={metrics?.pendingOutbound && metrics.pendingOutbound > 100 ? 'amber' : 'blue'}
        />
      </div>

      {/* Storage and Health Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StorageBar
          used={metrics?.storageUsedBytes ?? 0}
          total={metrics?.storageLimitBytes ?? 1}
          percentUsed={metrics?.storagePercentUsed ?? 0}
        />
        <SystemHealthCard health={health} isLoading={healthLoading} />
      </div>

      {/* Queue Health Panel */}
      <QueueHealthPanel inbound={inboundQueueHealth} outbound={outboundQueueHealth} onViewJobs={handleViewJobs} />

      {/* Security and Compliance Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard
          title="Active Clients (24h)"
          value={metricsLoading ? '-' : formatNumber(metrics?.activeClientsLast24h ?? 0)}
          icon={ComputerDesktopIcon}
          color="blue"
        />
        <StatCard
          title="Unauthorized Attempts (24h)"
          value={metricsLoading ? '-' : formatNumber(metrics?.unauthorizedClientAttempts24h ?? 0)}
          icon={ShieldExclamationIcon}
          color={metrics?.unauthorizedClientAttempts24h && metrics.unauthorizedClientAttempts24h > 10 ? 'red' : 'amber'}
        />
        <StatCard
          title="Failed Logins (24h)"
          value={metricsLoading ? '-' : formatNumber(metrics?.failedLoginsLast24h ?? 0)}
          icon={ShieldCheckIcon}
          color={metrics?.failedLoginsLast24h && metrics.failedLoginsLast24h > 10 ? 'red' : 'green'}
        />
        <StatCard
          title="Security Alerts"
          value={metricsLoading ? '-' : formatNumber(metrics?.suspiciousActivityAlerts ?? 0)}
          icon={ExclamationTriangleIcon}
          color={metrics?.suspiciousActivityAlerts && metrics.suspiciousActivityAlerts > 0 ? 'amber' : 'green'}
        />
        <StatCard
          title="Under Legal Hold"
          value={metricsLoading ? '-' : formatNumber(metrics?.messagesUnderLitigationHold ?? 0)}
          icon={ShieldCheckIcon}
          color="purple"
        />
        <StatCard
          title="Verified Domains"
          value={metricsLoading ? '-' : formatNumber(metrics?.verifiedDomains ?? 0)}
          subtitle={metricsLoading ? '' : `${metrics?.pendingDomains ?? 0} pending`}
          icon={ChartBarIcon}
          color="blue"
        />
      </div>

      {/* Alerts and Activity Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <AlertsList
          alerts={alerts ?? []}
          isLoading={alertsLoading}
          onAcknowledge={(alertId) => acknowledgeAlert.mutate(alertId)}
          acknowledging={acknowledgeAlert.isPending}
        />
        <ActivityList activities={activitySummary?.recentActions ?? []} isLoading={activityLoading} />
      </div>

      {/* Connected Clients */}
      <ConnectedClientsPanel clients={connectedClients ?? []} isLoading={clientsLoading} />

      {/* Top Storage Users */}
      <TopStorageUsers users={metrics?.topStorageUsers ?? []} isLoading={metricsLoading} />

      {/* Queue Job Viewer Modal */}
      <QueueJobViewerModal
        isOpen={queueViewerOpen}
        onClose={() => setQueueViewerOpen(false)}
        initialQueueType={queueViewerConfig.queueType}
        initialStatus={queueViewerConfig.status}
      />
    </div>
  );
};

export default AdminDashboardTab;
